import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { AuthService } from '../../../core/auth/auth.service';
import { GENERAL_EXPENSE_ACTION_ACCESS, canAccess } from '../../../core/auth/access-control';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { GeneralExpenseItem, GeneralExpenseService } from '../services/general-expense.service';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { StatusBadge } from '../../../shared/components/status-badge.component';

interface GeneralExpenseRow {
  id: string;
  expenseNo: string;
  expenseTypeId: string | null;
  expenseNameSnapshot: string;
  description: string;
  actualTransferAmount: number;
  paymentAccountSource: PaymentAccountSource | null;
  bankName: string | null;
  accountNumber: string | null;
  requestedBy: string;
  attachmentPath: string | null;
  attachmentUrl: string | null;
  attachmentName: string;
  receiptPath: string | null;
  receiptFileUrl: string | null;
  transferSlipUrl: string | null;
  status: string;
}

type PaymentAccountSource = 'REFERENCE_FILE' | 'OTHER_BANK_ACCOUNT';

@Component({
  selector: 'app-general-expenses-page',
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TextareaModule,
    FileUploadModule,
  ],
  templateUrl: './general-expenses-page.component.html',
})
export class GeneralExpensesPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly generalExpenseService = inject(GeneralExpenseService);

  readonly canManageGeneralExpenses = computed(() =>
    canAccess(this.auth.user(), GENERAL_EXPENSE_ACTION_ACCESS.manage),
  );
  readonly expenses = signal<GeneralExpenseRow[]>([]);
  readonly isLoading = signal(false);
  readonly isLoadingExpenseTypes = signal(false);
  readonly isModalOpen = signal(false);
  readonly isSaving = signal(false);
  readonly editingExpense = signal<GeneralExpenseRow | null>(null);
  readonly selectedExpense = signal<GeneralExpenseRow | null>(null);
  readonly attachmentName = signal('');
  readonly attachmentFile = signal<File | null>(null);
  readonly selectedReceiptExpense = signal<GeneralExpenseRow | null>(null);
  readonly receiptFile = signal<File | null>(null);
  readonly receiptFileName = signal('');
  readonly isSavingReceipt = signal(false);

  readonly expenseTypeOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly paymentAccountSourceOptions: Array<{
    label: string;
    value: PaymentAccountSource;
  }> = [
    { label: 'ตามไฟล์อ้างอิง', value: 'REFERENCE_FILE' },
    { label: 'บัญชีธนาคารอื่นๆ', value: 'OTHER_BANK_ACCOUNT' },
  ];
  readonly bankOptions: Array<{ label: string; value: string }> = [
    { label: 'ธนาคารกรุงเทพ', value: 'ธนาคารกรุงเทพ' },
    { label: 'ธนาคารกสิกรไทย', value: 'ธนาคารกสิกรไทย' },
    { label: 'ธนาคารกรุงไทย', value: 'ธนาคารกรุงไทย' },
    { label: 'ธนาคารไทยพาณิชย์', value: 'ธนาคารไทยพาณิชย์' },
    { label: 'ธนาคารกรุงศรีอยุธยา', value: 'ธนาคารกรุงศรีอยุธยา' },
    { label: 'ธนาคารทหารไทยธนชาต', value: 'ธนาคารทหารไทยธนชาต' },
    { label: 'ธนาคารออมสิน', value: 'ธนาคารออมสิน' },
    {
      label: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร',
      value: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร',
    },
    { label: 'ธนาคารอาคารสงเคราะห์', value: 'ธนาคารอาคารสงเคราะห์' },
    { label: 'ธนาคารซีไอเอ็มบี ไทย', value: 'ธนาคารซีไอเอ็มบี ไทย' },
    { label: 'ธนาคารยูโอบี', value: 'ธนาคารยูโอบี' },
    { label: 'ธนาคารเกียรตินาคินภัทร', value: 'ธนาคารเกียรตินาคินภัทร' },
    { label: 'ธนาคารทิสโก้', value: 'ธนาคารทิสโก้' },
    { label: 'ธนาคารไอซีบีซี (ไทย)', value: 'ธนาคารไอซีบีซี (ไทย)' },
  ];
  readonly expenseForm = this.fb.group({
    paymentAccountSource: [null as PaymentAccountSource | null, Validators.required],
    bankName: [null as string | null],
    accountNumber: [''],
    expenseTypeId: ['', Validators.required],
    description: ['', [Validators.required, Validators.maxLength(250)]],
    actualTransferAmount: [null as number | null, [Validators.required, Validators.min(1)]],
  });

  ngOnInit(): void {
    void this.refreshExpenses();
  }

  async refreshExpenses(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    try {
      const [, expenses] = await Promise.all([
        this.loadExpenseTypes(),
        this.generalExpenseService.getAll(),
      ]);
      this.expenses.set(expenses.map((expense) => this.toExpenseRow(expense)));
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดรายการเบิกไม่สำเร็จ', message);
    } finally {
      this.isLoading.set(false);
    }
  }

  openCreateModal(): void {
    if (!this.canManageGeneralExpenses()) return;
    this.editingExpense.set(null);
    this.expenseForm.reset({
      paymentAccountSource: null,
      bankName: null,
      accountNumber: '',
      expenseTypeId: '',
      description: '',
      actualTransferAmount: null,
    });
    this.updatePaymentAccountValidators(null);
    this.clearAttachment();
    this.isModalOpen.set(true);
  }

  openEditModal(expense: GeneralExpenseRow): void {
    if (!this.canManageGeneralExpenses() || expense.status !== 'DRAFT') return;
    this.editingExpense.set(expense);
    const paymentAccountSource = expense.paymentAccountSource;
    this.expenseForm.reset({
      paymentAccountSource,
      bankName: expense.bankName,
      accountNumber: expense.accountNumber ?? '',
      expenseTypeId:
        expense.expenseTypeId ??
        this.expenseTypeOptions().find((option) => option.label === expense.expenseNameSnapshot)
          ?.value ??
        '',
      description: expense.description,
      actualTransferAmount: expense.actualTransferAmount,
    });
    this.updatePaymentAccountValidators(paymentAccountSource);
    this.attachmentName.set(expense.attachmentName);
    this.attachmentFile.set(null);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    if (!this.isSaving()) {
      this.isModalOpen.set(false);
      this.editingExpense.set(null);
    }
  }

  selectAttachment(event: FileSelectEvent): void {
    const attachment = event.files[0] ?? null;
    this.attachmentFile.set(attachment);
    this.attachmentName.set(attachment?.name ?? '');
  }

  clearAttachment(): void {
    this.attachmentFile.set(null);
    this.attachmentName.set('');
  }

  onPaymentAccountSourceChange(source: PaymentAccountSource | null): void {
    this.updatePaymentAccountValidators(source);
  }

  private updatePaymentAccountValidators(source: PaymentAccountSource | null): void {
    const bankNameControl = this.expenseForm.controls.bankName;
    const accountNumberControl = this.expenseForm.controls.accountNumber;

    if (source === 'OTHER_BANK_ACCOUNT') {
      bankNameControl.setValidators(Validators.required);
      accountNumberControl.setValidators([
        Validators.required,
        Validators.pattern(/^\d{6,20}$/),
      ]);
    } else {
      bankNameControl.clearValidators();
      accountNumberControl.clearValidators();
      bankNameControl.setValue(null);
      accountNumberControl.setValue('');
    }

    bankNameControl.updateValueAndValidity();
    accountNumberControl.updateValueAndValidity();
  }

  openReceiptModal(expense: GeneralExpenseRow): void {
    if (!this.canManageGeneralExpenses() || expense.status !== 'WAITING_CLEARING') return;
    this.selectedReceiptExpense.set(expense);
    this.receiptFile.set(null);
    this.receiptFileName.set('');
  }

  closeReceiptModal(): void {
    if (this.isSavingReceipt()) return;
    this.selectedReceiptExpense.set(null);
    this.receiptFile.set(null);
    this.receiptFileName.set('');
  }

  selectReceipt(event: FileSelectEvent): void {
    const receipt = event.files[0] ?? null;
    this.receiptFile.set(receipt);
    this.receiptFileName.set(receipt?.name ?? '');
  }

  async saveReceipt(): Promise<void> {
    if (!this.canManageGeneralExpenses()) return;
    const expense = this.selectedReceiptExpense();
    const receipt = this.receiptFile();
    if (!expense || !receipt) return;

    this.isSavingReceipt.set(true);
    try {
      await this.generalExpenseService.submitReceipt(expense.id, receipt);
      this.isSavingReceipt.set(false);
      this.closeReceiptModal();
      await this.refreshExpenses();
      this.overlay.success('ส่งใบเสร็จสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('ส่งใบเสร็จไม่สำเร็จ', message);
    } finally {
      this.isSavingReceipt.set(false);
    }
  }

  isInvalid(field: keyof typeof this.expenseForm.controls): boolean {
    const control = this.expenseForm.controls[field];
    return control.touched && control.invalid;
  }

  async saveExpense(submitForReview = false): Promise<void> {
    if (!this.canManageGeneralExpenses()) return;
    if (this.expenseForm.invalid || !this.attachmentName()) {
      this.expenseForm.markAllAsTouched();
      return;
    }

    const value = this.expenseForm.getRawValue();
    if (!value.expenseTypeId || value.actualTransferAmount === null) return;

    const editing = this.editingExpense();
    const input = {
      paymentAccountSource: value.paymentAccountSource!,
      ...(value.paymentAccountSource === 'OTHER_BANK_ACCOUNT' && value.bankName
        ? { bankName: value.bankName }
        : {}),
      ...(value.paymentAccountSource === 'OTHER_BANK_ACCOUNT' && value.accountNumber
        ? { accountNumber: value.accountNumber }
        : {}),
      expenseTypeId: value.expenseTypeId,
      description: value.description ?? '',
      actualTransferAmount: value.actualTransferAmount,
      ...(submitForReview ? { submitForReview: true as const } : {}),
    };
    this.isSaving.set(true);
    try {
      const expense = editing
        ? await this.generalExpenseService.update(editing.id, input, this.attachmentFile())
        : await this.generalExpenseService.create(input, this.attachmentFile());
      const saved = this.toExpenseRow(expense);
      this.expenses.update((rows) =>
        editing ? rows.map((row) => (row.id === editing.id ? saved : row)) : [saved, ...rows],
      );
      this.isSaving.set(false);
      this.closeModal();
      this.overlay.success(submitForReview ? 'ส่งรายการเบิกสำเร็จ' : 'บันทึกร่างสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึกรายการเบิกไม่สำเร็จ', message);
    } finally {
      this.isSaving.set(false);
    }
  }

  async deleteExpense(expense: GeneralExpenseRow): Promise<void> {
    if (!this.canManageGeneralExpenses() || !['DRAFT', 'REJECTED'].includes(expense.status)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ลบรายการเบิกค่าใช้จ่ายทั่วไป',
      message: `ต้องการลบ ${expense.expenseNo} พร้อมไฟล์ที่แนบทั้งหมดหรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบรายการและไฟล์แนบ...');
    try {
      await this.generalExpenseService.delete(expense.id);
      this.expenses.update((rows) => rows.filter((row) => row.id !== expense.id));
      this.overlay.success('ลบรายการเบิกสำเร็จ', `${expense.expenseNo} และไฟล์แนบถูกลบแล้ว`);
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      if (error instanceof HttpErrorResponse && error.status === 409) {
        await this.refreshExpenses();
      }
      this.overlay.error('ลบรายการเบิกไม่สำเร็จ', message);
    } finally {
      this.overlay.hideLoading();
    }
  }

  private async loadExpenseTypes(): Promise<void> {
    if (this.isLoadingExpenseTypes()) return;

    this.isLoadingExpenseTypes.set(true);
    try {
      const expenseTypes = await this.generalExpenseService.getExpenseTypes();
      this.expenseTypeOptions.set(
        expenseTypes.map((expenseType) => ({ label: expenseType.name, value: expenseType.id })),
      );
    } catch {
      this.expenseTypeOptions.set([]);
      this.overlay.error('โหลดประเภทค่าใช้จ่ายไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.isLoadingExpenseTypes.set(false);
    }
  }

  private currentUserName(): string {
    const user = this.auth.user();
    return user ? `${user.firstName} ${user.lastName}`.trim() : 'ผู้ใช้งานปัจจุบัน';
  }

  private toExpenseRow(expense: GeneralExpenseItem): GeneralExpenseRow {
    return {
      id: expense.id,
      expenseNo: expense.expenseNo,
      expenseTypeId: expense.expenseTypeId,
      expenseNameSnapshot: expense.expenseNameSnapshot,
      description: expense.description,
      actualTransferAmount: expense.actualTransferAmount,
      paymentAccountSource: expense.paymentAccountSource,
      bankName: expense.bankName,
      accountNumber: expense.accountNumber,
      requestedBy: expense.requestedByName || this.currentUserName(),
      attachmentPath: expense.attachmentPath,
      attachmentUrl: this.generalExpenseService.fileUrl(expense.attachmentPath),
      attachmentName: this.attachmentName() || this.fileNameFromPath(expense.attachmentPath),
      receiptPath: expense.receiptPath,
      receiptFileUrl: this.generalExpenseService.fileUrl(expense.receiptPath),
      transferSlipUrl: this.generalExpenseService.fileUrl(expense.receiptUrl),
      status: expense.status,
    };
  }

  private fileNameFromPath(path: string | null): string {
    return path?.split('/').pop() ?? '';
  }
}
